/**
 * JAPAP Messenger — Realtime Notification Context
 * ================================================
 * Opens a single global Socket.IO connection for realtime events
 * (tip, like, comment, money). Displays sonner toasts with anti-spam
 * coalescing on low-priority likes and exposes a live unread count
 * for the bottom-nav badge.
 */
import { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { io } from 'socket.io-client';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { useCurrency } from '@/utils/currency';
import { formatMoney } from '@/utils/currency';

const API = process.env.REACT_APP_BACKEND_URL;

const RealtimeContext = createContext(null);

/**
 * Build a short "Xxx + N autres" label for coalesced like events.
 */
function coalesceLabel(senders) {
  const names = senders.map(s => s.name).filter(Boolean);
  if (names.length === 0) return 'Quelqu\'un';
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} et ${names[1]}`;
  return `${names[0]} et ${names.length - 1} autres`;
}

export function RealtimeProvider({ children }) {
  const { user } = useAuth();
  const { t } = useTranslation();
  const currency = useCurrency();
  const [unreadCount, setUnreadCount] = useState(0);
  // iter252b — Separate state for missed-call notifications so the
  // Messenger conversation can show an inline "Rappeler" CTA with the
  // caller context (id, name, avatar, type), and the messenger nav icon
  // can show a distinct badge. The global `unreadCount` already covers
  // the bottom-nav badge; this adds richer context.
  const [missedCalls, setMissedCalls] = useState({ count: 0, latest: null });
  const socketRef = useRef(null);
  // iter242a-livefeed — expose the live Socket.IO instance to consumers
  // via state so they can subscribe to extra events (e.g. notify_new_post).
  const [socket, setSocket] = useState(null);
  // Degraded = real outage (offline, or HTTP fallback failing), not mere fallback use.
  const [degraded, setDegraded] = useState(false);
  // Group likes per post within a 3s window to avoid spam
  const likeBufferRef = useRef(new Map()); // target_id -> { timer, senders: [] }

  /** Refresh unread count from server (used on mount + after marking read). */
  const refreshUnread = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/notifications/?page=1&limit=1`, { withCredentials: true });
      setUnreadCount(data.unread || 0);
    } catch { /* silent */ }
  }, []);

  /** iter252b — Refresh missed-call count + latest. Polled every 60s. */
  const refreshMissedCalls = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/calls/missed/count`, { withCredentials: true });
      setMissedCalls({
        count: data.count || 0,
        latest: data.latest || null,
      });
    } catch { /* silent */ }
  }, []);

  /** iter252b — Dismiss missed-call notifications (called by conversation
   * when user clicks Rappeler or opens the missed call). */
  const dismissMissedCalls = useCallback(async () => {
    try {
      await axios.post(`${API}/api/calls/missed/dismiss`, {}, { withCredentials: true });
      setMissedCalls({ count: 0, latest: null });
      refreshUnread();
    } catch { /* silent */ }
  }, [refreshUnread]);

  const flushLikeBuffer = useCallback((targetId) => {
    const buf = likeBufferRef.current.get(targetId);
    if (!buf) return;
    likeBufferRef.current.delete(targetId);
    const n = buf.senders.length;
    const label = coalesceLabel(buf.senders);
    toast(
      n > 1 ? `${label} ont aimé votre publication` : `${label} a aimé votre publication`,
      { icon: '❤️', duration: 3500 }
    );
  }, []);

  useEffect(() => {
    if (!user) return;
    // iter284 Phase C — Skip long-polling transport upgrade dance. Force
    // websocket-only so each session opens ONE upgrade → ONE ws frame
    // rather than 6-8 polling handshakes. Aligns with the Cloudflare/
    // ingress config which allows ws:// on this route.
    // Perf — single shared connection (Call/Chat/Tasks reuse it). WebSocket
    // first; polling only as a fallback when WS is blocked. Bounded backoff
    // (1s → 10s, jitter) + immediate retry when the network/tab comes back.
    const sock = io(API, {
      path: '/api/socket.io',
      // Engine.IO long-polling cannot work across 2 servers without session
      // affinity; the HTTP fallback below (useRealtimeHttpFallback) replaces it.
      transports: ['websocket'],
      upgrade: false,
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      randomizationFactor: 0.5,
    });
    socketRef.current = sock;
    installHttpEmit(sock);
    setSocket(sock);
    const kick = () => {
      if (sock.connected || navigator.onLine === false) return;
      sock.disconnect();
      sock.connect();
    };
    const onVisible = () => { if (document.visibilityState === 'visible') kick(); };
    window.addEventListener('online', kick);
    document.addEventListener('visibilitychange', onVisible);

    sock.on('connect', () => {
      const token = document.cookie.split(';').find(c => c.trim().startsWith('access_token='))?.split('=')[1];
      if (token) sock.emit('authenticate', { token });
    });

    // High priority: Tip received
    // iter265e P0 — Strict Wallet currency rule. Previously hardcoded "XAF"
    // string (and French copy) in the toast. Now we (a) translate via t(),
    // (b) format the amount through formatMoney(USD reference → user local
    // currency with USD hint). The backend payload sends `amount` as the USD
    // reference value; `currency` may be present but is treated as legacy.
    sock.on('notify_tip', (d) => {
      const name = d?.sender?.name || t('common.someone', { defaultValue: 'Quelqu\'un' });
      const usd = Number(d?.amount) || 0;
      const pretty = formatMoney(usd, currency, { short: false, usdFirst: true });
      toast.success(t('realtime.tip_received_title', {
          defaultValue: '{{name}} vous a envoyé {{amount}} 🎁',
          name, amount: pretty,
        }), {
        description: d?.message
          ? `"${d.message}"`
          : t('realtime.tip_received_desc', { defaultValue: 'Tip reçu sur votre contenu' }),
        duration: 6000,
      });
      setUnreadCount(c => c + 1);
    });

    // High priority: Chat money received
    sock.on('notify_money', (d) => {
      const name = d?.sender?.name || t('common.someone', { defaultValue: 'Quelqu\'un' });
      const usd = Number(d?.amount) || 0;
      const pretty = formatMoney(usd, currency, { short: false, usdFirst: true });
      toast.success(`💸 ${t('realtime.money_received_title', {
          defaultValue: '{{name}} vous a envoyé {{amount}}',
          name, amount: pretty,
        })}`, {
        description: d?.note
          ? `"${d.note}"`
          : t('realtime.money_received_desc', { defaultValue: 'Argent reçu dans Messenger' }),
        duration: 6000,
      });
      setUnreadCount(c => c + 1);
    });

    // High priority: JAPAP Connect revshare credit received
    sock.on('notify_connect_revshare', (d) => {
      const ccy = d?.currency || 'USD';
      const amountLocal = d?.amount_local;
      const display = amountLocal && ccy !== 'USD'
        ? `${amountLocal} ${ccy}`
        : `$${d?.amount_usd || '0'}`;
      const plan = d?.plan_name ? ` (${d.plan_name})` : '';
      const pctLabel = d?.pct ? `${d.pct}%` : 'votre part';
      toast.success(`🎉 Nouveau gain reçu — +${display}${plan}`, {
        description: `${pctLabel} JAPAP Pro via Connect. Continuez à partager votre WiFi !`,
        duration: 7000,
      });
      setUnreadCount(c => c + 1);
    });

    // Medium priority: Comment
    sock.on('notify_comment', (d) => {
      const name = d?.sender?.name || 'Quelqu\'un';
      toast(`${name} a commenté votre publication`, {
        icon: '💬',
        description: d?.preview || '',
        duration: 4500,
      });
      setUnreadCount(c => c + 1);
    });

    // Low priority: Like — coalesce per target within 3s
    sock.on('notify_like', (d) => {
      const targetId = d?.target_id;
      if (!targetId) return;
      const sender = { name: d?.sender?.name || 'Quelqu\'un' };
      const existing = likeBufferRef.current.get(targetId);
      if (existing) {
        existing.senders.push(sender);
      } else {
        const timer = setTimeout(() => flushLikeBuffer(targetId), 3000);
        likeBufferRef.current.set(targetId, { timer, senders: [sender] });
      }
      setUnreadCount(c => c + 1);
    });

    // iter252b — Live update of missed-call badge: backend emits this
    // event when a call ends with status=missed/rejected and the user
    // is the callee. Frontend bumps the count + caches the latest entry
    // so the in-conversation "Rappeler" banner shows without polling.
    sock.on('notify_call_missed', (d) => {
      setMissedCalls((prev) => ({
        count: (prev.count || 0) + 1,
        latest: {
          notif_id: d?.notif_id || '',
          title: d?.title || 'Appel manqué',
          message: d?.message || '',
          created_at: d?.created_at || new Date().toISOString(),
          data: {
            caller_id: d?.caller_id || '',
            caller_name: d?.caller_name || '',
            caller_avatar: d?.caller_avatar || '',
            call_type: d?.call_type || 'audio',
            call_id: d?.call_id || '',
          },
        },
      }));
      setUnreadCount(c => c + 1);
    });

    return () => {
      // Flush any pending like buffers before unmount
      likeBufferRef.current.forEach(buf => clearTimeout(buf.timer));
      likeBufferRef.current.clear();
      window.removeEventListener('online', kick);
      document.removeEventListener('visibilitychange', onVisible);
      try { sock.disconnect(); } catch { /* no-op */ }
      setSocket(null);
    };
    // Keyed on user_id: a refreshed user object must not recycle the socket.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, flushLikeBuffer]);

  useRealtimeHttpFallback(socket, setDegraded);

  // Initial + periodic fallback sync
  // iter284 P.B — Page Visibility guard : ne polle plus notifications +
  // missed-calls tant que l'onglet est masqué. Refresh immédiat au retour.
  useEffect(() => {
    if (!user) {
      setUnreadCount(0);
      setMissedCalls({ count: 0, latest: null });
      return;
    }
    refreshUnread();
    refreshMissedCalls();
    let id = null;
    const tick = () => { refreshUnread(); refreshMissedCalls(); };
    const start = () => {
      if (id) return;
      id = setInterval(tick, 60000);
    };
    const stop = () => { if (id) { clearInterval(id); id = null; } };
    const onVis = () => {
      if (document.visibilityState === 'visible') { tick(); start(); }
      else stop();
    };
    if (typeof document === 'undefined' || document.visibilityState === 'visible') start();
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis);
    };
  }, [user, refreshUnread, refreshMissedCalls]);

  return (
    <RealtimeContext.Provider value={useMemo(() => ({
      unreadCount, refreshUnread, setUnreadCount,
      missedCalls, refreshMissedCalls, dismissMissedCalls,
      socket, socketRef, degraded,
    }), [unreadCount, refreshUnread, missedCalls, refreshMissedCalls, dismissMissedCalls, socket, degraded])}>
      {degraded && <DegradedBadge />}
      {children}
    </RealtimeContext.Provider>
  );
}

// ── HTTP fallback when WebSocket is blocked ──────────────────────────────
const HTTP_EVENTS = new Set(['call_invite', 'call_accept', 'call_reject', 'call_end', 'mark_delivered',
  'typing', 'stop_typing', 'group_call_join', 'group_call_leave']);

// Client→server events go through POST /api/realtime/emit while disconnected.
function installHttpEmit(sock) {
  const rawEmit = sock.emit.bind(sock);
  sock.__convs = new Set();
  sock.emit = (ev, ...args) => {
    if (ev === 'join_conversation' && args[0]?.conv_id) sock.__convs.add(args[0].conv_id);
    if (ev === 'leave_conversation' && args[0]?.conv_id) sock.__convs.delete(args[0].conv_id);
    if (!sock.connected && HTTP_EVENTS.has(ev)) {
      const cb = typeof args[args.length - 1] === 'function' ? args.pop() : null;
      axios.post(`${API}/api/realtime/emit`, { event: ev, data: args[0] || {} }, { withCredentials: true })
        .then(r => cb && cb(r.data?.result ?? { ok: true }))
        .catch(() => cb && cb({ ok: false, error: 'http_fallback_failed' }));
      return sock;
    }
    return rawEmit(ev, ...args);
  };
}

// Server→client events: poll the cross-server journal and dispatch them to
// the listeners already registered on the shared socket. A working fallback
// is NOT a problem: the badge only shows on real degradation (device offline
// or the fallback itself failing twice in a row) and clears on recovery.
const FALLBACK_FAILS_BEFORE_BADGE = 2;
function useRealtimeHttpFallback(sock, setDegraded) {
  useEffect(() => {
    if (!sock) return undefined;
    let timer = null; let poller = null; let cursor = {}; let stopped = false; let fails = 0; let lost = false;
    // Tell pages to fetch what they missed and resend their queue.
    const resync = () => { lost = false; sock.listeners('jp_resync').forEach(fn => { try { fn(); } catch { /* listener error */ } }); };
    const evaluate = () => setDegraded(!sock.connected && (navigator.onLine === false || fails >= FALLBACK_FAILS_BEFORE_BADGE));
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/api/realtime/poll`, {
          params: { convs: [...(sock.__convs || [])].join(','), cursor: JSON.stringify(cursor) },
          withCredentials: true, timeout: 10000,
        });
        cursor = data.cursor || cursor;
        if (fails > 0) resync();
        fails = 0;
        (data.events || []).forEach(e => sock.listeners(e.event).forEach(fn => { try { fn(e.data); } catch { /* listener error */ } }));
      } catch { fails += 1; }
      evaluate();
    };
    const startFallback = () => {
      if (poller || stopped) return;
      poll();
      poller = setInterval(poll, 2500);
    };
    const stopFallback = () => {
      if (lost) resync();
      sock.__everConnected = true;
      clearTimeout(timer); timer = null;
      if (poller) { clearInterval(poller); poller = null; cursor = {}; }
      fails = 0;
      setDegraded(false);
    };
    const arm = () => { if (!sock.connected && sock.__everConnected) lost = true; evaluate(); if (!sock.connected && !timer && !poller) timer = setTimeout(startFallback, 6000); };
    sock.on('connect', stopFallback);
    sock.on('connect_error', arm);
    sock.on('disconnect', arm);
    window.addEventListener('offline', evaluate);
    window.addEventListener('online', evaluate);
    arm();
    return () => {
      stopped = true; stopFallback();
      sock.off('connect', stopFallback); sock.off('connect_error', arm); sock.off('disconnect', arm);
      window.removeEventListener('offline', evaluate);
      window.removeEventListener('online', evaluate);
    };
  }, [sock, setDegraded]);
}

function DegradedBadge() {
  const { t } = useTranslation();
  return (
    <div role="status" aria-live="polite" data-testid="realtime-degraded-badge"
         title={t('realtime.degraded_hint')}
         className="fixed top-2 left-1/2 -translate-x-1/2 z-[80] flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold shadow-md pointer-events-auto"
         style={{ background: '#F59E0B', color: '#1F1300' }}>
      <span className="h-1.5 w-1.5 rounded-full animate-pulse" style={{ background: '#7C2D12' }} />
      {t('realtime.degraded_badge')}
    </div>
  );
}

export function useRealtime() {
  const ctx = useContext(RealtimeContext);
  if (!ctx) return {
    unreadCount: 0, refreshUnread: () => {}, setUnreadCount: () => {},
    missedCalls: { count: 0, latest: null }, refreshMissedCalls: () => {}, dismissMissedCalls: () => {},
  };
  return ctx;
}
